/**
 * Utility functions for user state and configuration management
 * Centralizes logic for determining user types and generating UI configurations
 */
import { SUBSCRIPTION_STATUS } from '@/utils/subscriptionStatus';

/**
 * Get subscription message based on access level, subscription status, and date availability
 * @param {boolean} isAdmin - Whether user is admin
 * @param {string|null} endDateString - Formatted end date string (trial or subscription end date)
 * @param {string|null} planName - Plan name
 * @param {string|null} status - Subscription status identifier
 * @returns {string} Message string
 */
export function getMessage(isAdmin, endDateString, planName, status = null) {
    const hasDateAndPlan = Boolean(endDateString && planName);

    // Helper function to build admin upgrade prompt
    const getUpgradePrompt = () =>
        isAdmin ? 'To keep access, please upgrade your plan.' : '';

    // Helper function to build payment update prompt
    const getPaymentPrompt = () =>
        isAdmin ? 'Please update your payment method. ' : '';

    // Handle specific subscription statuses
    switch (status) {
        case SUBSCRIPTION_STATUS.TRIAL_WITH_CARD:
            return hasDateAndPlan
                ? `Your free trial will end on ${endDateString}, you'll be automatically billed for the ${planName}`
                : 'You are currently in a free trial period, after which you will be automatically billed.';

        case SUBSCRIPTION_STATUS.TRIAL_CANCELLED: {
            const trialCancelledPrompt = getUpgradePrompt();
            return endDateString
                ? `You have cancelled your free trial and will lose access on ${endDateString}. ${trialCancelledPrompt}`
                : `You have cancelled your free trial and will lose access after the trial period. ${trialCancelledPrompt}`;
        }

        case SUBSCRIPTION_STATUS.ACTIVE_CANCELLED: {
            const activeCancelledPrompt = getUpgradePrompt();
            return endDateString
                ? `You have cancelled your subscription and will lose access on ${endDateString}. ${activeCancelledPrompt}`
                : `You have cancelled your subscription and will lose access after the current billing period. ${activeCancelledPrompt}`;
        }
        case SUBSCRIPTION_STATUS.TRIAL_WITHOUT_CARD: {
            const paymentPrompt = getPaymentPrompt();
            return hasDateAndPlan
                ? `${paymentPrompt}On ${endDateString}, you'll be automatically billed for the ${planName}.`
                : `${paymentPrompt}Your free trial is ending soon.`;
        }
        default:
            return isAdmin
                ? 'You are currently using a view only account. To unlock full access, upgrade your plan.'
                : 'You are currently using a view only account.';
    }
}

/**
 * Returns configuration details for user subscription state in UI components.
 *
 * @param {Object} options - Options to generate the config.
 * @param {Object} options.subscriptionDetails - Raw subscription details object.
 * @param {boolean} options.isCompanyAdmin - True if the user is a company admin.
 * @param {string|null} options.freeTrialEndDateString - Readable end date for the free trial.
 * @param {string|null} options.planName - Display name for the subscription plan.
 * @returns {Object|null} A configuration object including UI titles, messages, button labels, and actions
 */
export function getUserSubscriptionConfig({
    subscriptionDetails,
    isCompanyAdmin,
    freeTrialEndDateString,
    planName,
} = {}) {
    if (!subscriptionDetails) return null;

    const status = subscriptionDetails.status;
    const message = getMessage(
        isCompanyAdmin,
        freeTrialEndDateString,
        planName,
        status
    );

    switch (status) {
        case SUBSCRIPTION_STATUS.VIEW_ONLY:
            return {
                title: 'View Only Access',
                message,
                buttonText: 'Unlock Full Access',
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: 'Upgrade your plan',
                              icon: 'fi-eye',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                          },
                      ]
                    : [],
                type: 'WARNING',
            };

        case SUBSCRIPTION_STATUS.TRIAL_WITH_CARD:
            return {
                title: 'Free Trial Ending Soon',
                message,
                buttonText: 'View Billing',
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: 'View Billing',
                              icon: 'fi-eye',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                          },
                      ]
                    : [],
                type: 'INFO',
            };

        case SUBSCRIPTION_STATUS.TRIAL_WITHOUT_CARD:
            return {
                title: 'Free Trial Ending Soon',
                message,
                buttonText: 'Update Payment Method',
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: 'Update Payment Method',
                              icon: 'fi-chevron-right',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                              actionType: 'openPayment',
                          },
                      ]
                    : [],
                type: 'WARNING',
            };
        case SUBSCRIPTION_STATUS.TRIAL_CANCELLED:
            return {
                title: 'Free Trial Cancelled',
                message,
                buttonText: 'View Plans',
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: 'View Plans',
                              icon: 'fi-chevron-right',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                          },
                      ]
                    : [],
                type: 'WARNING',
            };
        case SUBSCRIPTION_STATUS.ACTIVE_CANCELLED:
            return {
                title: 'Subscription Ending Soon',
                message,
                buttonText: 'View Plans',
                actionButtons: isCompanyAdmin
                    ? [
                          {
                              label: 'View Plans',
                              icon: 'fi-chevron-right',
                              class: 'btn-secondary',
                              click: 'goToPlans',
                          },
                      ]
                    : [],
                type: 'WARNING',
            };

        default:
            return null;
    }
}

/**
 * Get free trial persistent notification configuration
 * This is specifically for notification components (NotificationPanel, NotificationsCard)
 * @param {Object} permStore - Permission store instance
 * @param {Object} userStore - User store instance
 * @param {string|null} freeTrialEndDateString - Formatted end date string
 * @param {string|null} planName - Plan name
 * @returns {Object|null} Notification configuration object or null
 */
export function getFreeTrialPersistentNotification({
    subscriptionDetails,
    isCompanyAdmin,
    freeTrialEndDateString,
    planName,
} = {}) {
    if (!subscriptionDetails) return null;

    const hasCard = Boolean(subscriptionDetails.hasCard);
    const isInTrial = Boolean(subscriptionDetails.isInTrial);

    if (!isCompanyAdmin || !isInTrial || hasCard) {
        return null;
    }

    return {
        title: 'Free Trial Ending Soon',
        message: getMessage(
            isCompanyAdmin,
            freeTrialEndDateString,
            planName,
            subscriptionDetails.status
        ),
        type: 'WARNING',
        showClearBtn: false,
        actionButtons: [
            {
                label: 'Update Payment Method',
                icon: 'fi-chevron-right',
                class: 'btn-primary',
                click: 'goToPlans',
                actionType: 'openPayment',
            },
        ],
    };
}
